import { PointG2, Fr } from '@noble/bls12-381';

/**
 * Aggregation of Verifiable Secret Sharing (VSS) keyshares for distributed IBE.
 *
 * The IBE private key for an identity is d_ID = s · H(ID), a point in BLS12-381 G2
 * (this is what `ts-ibe`'s `timelockDecrypt` parses with `PointG2.fromHex`).
 *
 * When the master secret `s` is shared with a Shamir/Feldman polynomial f (f(0) = s),
 * each party i holds a keyshare sigma_i = f(i) · H(ID), also a G2 point. To recover the
 * real key we interpolate the shares "in the exponent" at x = 0:
 *
 *     d_ID = sum_{i in S} lambda_i · sigma_i
 *
 * where lambda_i is the Lagrange basis coefficient evaluated at 0, computed modulo the
 * scalar field order r (BLS12-381 Fr):
 *
 *     lambda_i = prod_{j in S, j != i} (0 - x_j) / (x_i - x_j)   (mod r)
 */

// BLS12-381 scalar field (group) order r.
const ORDER = Fr.ORDER;

/** Reduce a bigint into the range [0, ORDER). */
function modOrder(x) {
  const r = x % ORDER;
  return r < 0n ? r + ORDER : r;
}

/** Modular inverse of `a` modulo `m` via the extended Euclidean algorithm. */
function modInverse(a, m) {
  let [oldR, r] = [modOrder(a), m];
  let [oldS, s] = [1n, 0n];
  while (r !== 0n) {
    const q = oldR / r;
    [oldR, r] = [r, oldR - q * r];
    [oldS, s] = [s, oldS - q * s];
  }
  if (oldR !== 1n) {
    throw new Error('Keyshare index is not invertible modulo the group order');
  }
  return modOrder(oldS);
}

/**
 * Lagrange basis coefficient for index `i` evaluated at x = 0, over the given index set.
 * @param {bigint} i - This share's index (x-coordinate).
 * @param {bigint[]} indices - All share indices in the set.
 * @returns {bigint} lambda_i in [0, ORDER)
 */
function lagrangeCoefficientAtZero(i, indices) {
  let numerator = 1n;
  let denominator = 1n;
  for (const j of indices) {
    if (j === i) continue;
    numerator = modOrder(numerator * modOrder(-j));
    denominator = modOrder(denominator * modOrder(i - j));
  }
  return modOrder(numerator * modInverse(denominator, ORDER));
}

/** Strip an optional `0x` prefix and whitespace from a hex string. */
function normalizeHex(hex) {
  if (typeof hex !== 'string') {
    throw new Error('Keyshare must be a hex string');
  }
  const trimmed = hex.trim();
  return trimmed.startsWith('0x') ? trimmed.slice(2) : trimmed;
}

/**
 * Aggregate VSS keyshares into the IBE private key via Lagrange interpolation over G2.
 *
 * @param {Array<{ index: (number|string|bigint), keyHex: string }>} shares
 *   Each share's `keyHex` is a hex-encoded BLS12-381 G2 point (the decrypted keyshare),
 *   and `index` is its VSS x-coordinate (`encrypted_keyshare_index`).
 * @returns {string} Hex-encoded (compressed) G2 point: the reconstructed IBE private key.
 */
export function aggregateKeyshares(shares) {
  if (!Array.isArray(shares) || shares.length === 0) {
    throw new Error('At least one keyshare is required for aggregation');
  }

  const parsed = shares.map((s) => {
    if (s.index == null) {
      throw new Error('Each keyshare must have an index (encrypted_keyshare_index)');
    }
    return {
      index: BigInt(s.index),
      point: PointG2.fromHex(normalizeHex(s.keyHex))
    };
  });

  // Single share: nothing to interpolate — it is the key itself.
  if (parsed.length === 1) {
    return parsed[0].point.toHex(true);
  }

  const indices = parsed.map((p) => p.index);
  const seen = new Set();
  for (const idx of indices) {
    const key = idx.toString();
    if (seen.has(key)) {
      throw new Error(`Duplicate keyshare index ${key}; indices must be distinct`);
    }
    seen.add(key);
  }

  let accumulator = PointG2.ZERO;
  for (const { index, point } of parsed) {
    const lambda = lagrangeCoefficientAtZero(index, indices);
    // `multiply` rejects a zero scalar; a zero coefficient contributes nothing.
    if (lambda === 0n) continue;
    accumulator = accumulator.add(point.multiply(lambda));
  }

  if (accumulator.isZero()) {
    throw new Error('Aggregated keyshare is the identity point; invalid shares');
  }

  return accumulator.toHex(true);
}
